/* eslint-disable class-methods-use-this */
import {
	Module, Mutation, VuexModule, Action,
} from 'vuex-module-decorators';
import { Session } from '@/core/types/session';
import { SessionUser } from '@/core/types/session-user';
import { CoreAPIService } from '@/core/services/api';
import { ViewportDimensions } from '@/core/types/viewport-dimensions';
import { Plaid } from '@/core/types/plaid';

declare let pendo: any;

@Module({
	namespaced: true,
	name: 'core',
})
export default class CoreStore extends VuexModule {
	public isInitialized = false;

	public isPageErrorShown = false;

	public isPageLoaderShown = false;

	public session: Session | null = null;

	public windowResizeEvent: UIEvent | null = null;

	public viewportDimensions = {};

	get plaid(): Plaid {
		return this.session?.plaid as Plaid;
	}

	get user(): SessionUser {
		return this.session?.user as SessionUser;
	}

	@Mutation
	public setIsInitialized(isInitialized: boolean): void {
		this.isInitialized = isInitialized;
	}

	@Mutation
	public setIsPageLoaderShown(isPageLoaderShown: boolean): void {
		this.isPageLoaderShown = isPageLoaderShown;
	}

	@Mutation
	public setIsPageErrorShown(isShown: boolean): void {
		this.isPageErrorShown = isShown;
	}

	@Mutation
	public setSession(session: Session): void {
		this.session = session;
	}

	@Mutation
	public updateSession(session: Partial<Session>): void {
		this.session = {
			...this.session,
			...session as Session,
		};
	}

	@Mutation
	public setViewportDimensions(dimensions: ViewportDimensions): void {
		this.viewportDimensions = dimensions;
	}

	@Mutation
	public setWindowResizeEvent(event: UIEvent): void {
		this.windowResizeEvent = event;
	}

	@Mutation
	public initializePendo(session: Session):void {
		pendo.initialize({
			visitor: {
				id: session.user.email, // Required if user is logged in, default creates anonymous ID
				email: session.user.email, // Recommended if using Pendo Feedback, or NPS Email
				full_name: session.user.name, // Recommended if using Pendo Feedback
				role: 'borrower', // Optional

				// You can add any additional visitor level key-values here,
				// as long as it's not one of the above reserved names.
			},

			account: {
				id: 'CONSUMER_WEB_VUE', // Required if using Pendo Feedback, default uses the value 'ACCOUNT-UNIQUE-ID'
				// name:         // Optional
				// is_paying:    // Recommended if using Pendo Feedback
				// monthly_value:// Recommended if using Pendo Feedback
				// planLevel:    // Optional
				// planPrice:    // Optional
				// creationDate: // Optional

				// You can add any additional account level key-values here,
				// as long as it's not one of the above reserved names.
			},
		});
	}

	@Action({ rawError: true })
	public async getAndSetSession(): Promise<void> {
		const session = await CoreAPIService.getSession();
		this.setSession(session);
		this.initializePendo(session);
	}

	@Action({ rawError: true })
	public initializeWindowResizeListener(): void {
		window.addEventListener('resize', (event) => {
			this.setWindowResizeEvent(event);
			this.setViewportDimensions({ width: window.innerWidth, height: window.innerHeight });
		});
	}
}
